"use client";

import { ShieldCheck, ChevronDown, Play, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheDigitalShield() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stats = [{
    label: "EST. 2026"
  }, {
    label: "AI-POWERED"
  }, {
    label: "24/7 PROTECTION"
  }];
  return <section data-section-id="870" id="the-digital-shield" className="relative bg-background text-foreground overflow-hidden min-h-[92vh] flex items-center">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse 60% 50% at 10% 90%, hsl(185 100% 50% / 0.18), transparent 60%)"
    }} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 pt-28 pb-24 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-7 order-2 lg:order-1 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex flex-wrap items-center gap-3 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground mb-8">
              <span className="relative flex items-center">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg opacity-75" style={{
                  backgroundColor: "hsl(185 100% 50%)"
                }} />
                  <span className="relative inline-flex h-2 w-2 rounded-lg" style={{
                  backgroundColor: "hsl(185 100% 50%)"
                }} />
                </span>
              </span>
              {stats.map((s, i) => <span key={i} data-index={i} className="flex items-center gap-3">
                  {i > 0 && <span className="text-muted-foreground/40">•</span>}
                  <span>{s.label}</span>
                </span>)}
            </div>

            {}
            <h1 className="font-default font-bold tracking-tight text-foreground leading-[1.02] mb-8" style={{
            fontSize: "clamp(48px, 9vw, 140px)",
            letterSpacing: "-0.02em"
          }}>
              AI-Powered Security for{" "}
              <span className="italic font-light">the modern</span>{" "}
              <span className="relative inline-block italic font-light">
                enterprise
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 20" fill="none" preserveAspectRatio="none" aria-hidden="true" style={{
                height: "0.5em"
              }}>
                  <path d="M2 12 Q 75 2, 150 10 T 298 8" stroke="hsl(185 100% 50%)" strokeWidth="3" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h1>

            {}
            <p className="font-default text-[18px] md:text-[20px] leading-relaxed text-muted-foreground max-w-[520px] mb-10">
              InfinitySecure protects your digital assets with advanced AI threat detection, 24/7 monitoring, and proactive risk management.
            </p>

            {}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <Button size="lg" className="rounded-lg h-14 px-7 bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-base shadow-lg">
                <ShieldCheck className="size-5" />
                Get Free Assessment
              </Button>

              <Button size="lg" variant="outline" className="rounded-lg h-14 px-7 bg-transparent border-border text-foreground hover:bg-foreground/5 hover:text-foreground font-medium text-base">
                <Lock className="size-5" />
                Contact Us
              </Button>
            </div>

            {}
            <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="size-3.5 bg-primary" />)}
              </div>
              <span>Trusted by 500+ firms</span>
              <span className="text-muted-foreground/40">•</span>
              <span>Industry Leading</span>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 order-1 lg:order-2 relative transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative lg:-mr-12 xl:-mr-24">
              {}
              <div className="absolute -inset-4 rounded-lg blur-2xl opacity-40" style={{
              background: "radial-gradient(circle, hsl(185 100% 50% / 0.4), transparent 70%)"
            }} aria-hidden="true" />

              <div className="relative rounded-lg overflow-hidden border border-border/60" style={{
              boxShadow: "0 0 0 1px hsl(185 100% 50% / 0.15), 0 30px 60px -15px hsl(0 0% 0% / 0.6)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/cyber_security_monitoring.png" alt="Cybersecurity professional monitoring network threats on a digital dashboard" loading="lazy" decoding="async" className="w-full h-[560px] md:h-[640px] object-cover" />
                {}
                <div className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(180deg, hsl(230 20% 4% / 0.3) 0%, transparent 30%, transparent 60%, hsl(230 20% 4% / 0.7) 100%)"
              }} aria-hidden="true" />
              </div>

              {}
              <div className="absolute bottom-6 left-6 right-6 sm:right-auto sm:max-w-sm">
                <div className="bg-background/60 backdrop-blur-lg border border-border/60 rounded-lg p-4 shadow-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center">
                        <ShieldCheck className="size-8 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-1.5">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-lg opacity-75" style={{
                          backgroundColor: "hsl(185 100% 50%)"
                        }} />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-lg" style={{
                          backgroundColor: "hsl(185 100% 50%)"
                        }} />
                        </span>
                        System Status
                      </div>
                      <div className="font-default font-semibold text-sm text-foreground truncate">
                        Threat Detection Active
                      </div>
                      <div className="font-default text-xs text-muted-foreground truncate">
                        Monitoring 24/7
                      </div>
                    </div>
                    <Button size="icon" className="rounded-lg h-12 w-12 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg flex-shrink-0" aria-label="View status">
                      <Play className="size-5 fill-current ml-0.5" />
                    </Button>
                  </div>

                  {}
                  <div className="space-y-1.5">
                    <div className="h-1 w-full bg-foreground/10 rounded-lg overflow-hidden">
                      <div className="h-full rounded-lg" style={{
                      width: "100%",
                      backgroundColor: "hsl(185 100% 50%)"
                    }} />
                    </div>
                    <div className="flex justify-between font-mono text-[10px] text-muted-foreground">
                      <span>SECURE</span>
                      <span>ONLINE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-10 left-0 right-0 pointer-events-none px-6 lg:px-12">
        <svg viewBox="0 0 1400 60" className="w-full h-10 opacity-30" preserveAspectRatio="none" aria-hidden="true">
          {Array.from({
          length: 120
        }).map((_, i) => {
          const h = 8 + Math.abs(Math.sin(i * 0.4)) * 40 + Math.abs(Math.cos(i * 0.27)) * 12;
          return <rect key={i} data-index={i} x={i * 12} y={30 - h / 2} width="3" height={h} rx="1.5" fill="currentColor" className="text-muted-foreground" style={{
            animation: `wave-pulse 2.4s ease-in-out ${i % 12 * 0.1}s infinite`
          }} />;
        })}
        </svg>
      </div>

      {}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        <span>Scroll</span>
        <ChevronDown className="size-3 animate-bounce" />
      </div>

      <style>{`
        @keyframes wave-pulse {
          0%, 100% { transform: scaleY(0.6); transform-origin: center; }
          50% { transform: scaleY(1.1); transform-origin: center; }
        }
      `}</style>
    </section>;
}